import {
  ComponentType,
  CSSProperties,
  Fragment,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
} from "react";
import { FilterArrows } from "../../assets/icons/filterArrows";
import {
  DataTableBody,
  DataTableEmptyMessage,
  DataTableLoadingCell,
  DataTableLoadingRow,
  DataTableLoadingSpinner,
  DataTableLoadingStatus,
  DataTableMessage,
  DataTableRoot,
} from "./DataTable.styles";
import { Pagination, PaginationProps } from "./Pagination";
import { DataGridHeaderCell, TableHeaderIconButton } from "./Table";

export {
  CompactDataTableCell,
  CompactDataTableHeader,
  CompactDataTableRow,
  DataTableAction,
  DataTableActionCell,
  DataTableCell,
  DataTableCellText,
  DataTableDestructiveText,
  DataTableFilterButton,
  DataTableFixedCells,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableHeaderRow,
  DataTableHeaderText,
  DataTableLoadingSpinner,
  DataTableLoadingStatus,
  DataTableMenu,
  DataTableMenuAnchor,
  DataTableMenuItem,
  DataTableMenuTrigger,
  DataTableMessage,
  DataTableRoot,
  DataTableRow,
  DataTableScrollableCells,
  DataTableVisuallyHidden,
  TablePage,
  TableSearchButton,
  TableSearchField,
  TableSearchRoot,
  TableToolbar,
} from "./DataTable.styles";
export type { DataTableResponsiveSize } from "./DataTable.types";

export type DataTableSortOrder = "asc" | "desc" | "ASC" | "DESC";

export interface DataTableSortHeaderProps {
  field: string;
  label: ReactNode;
  sortField?: string;
  sortOrder?: DataTableSortOrder | string;
  onSort: (field: string) => void;
  defaultSortOrder?: DataTableSortOrder;
  sortLabel?: string;
  component?: ComponentType<any>;
  headerProps?: Record<string, unknown>;
  iconComponent?: ComponentType<any>;
}

const getAriaSort = (field: string, sortField?: string, sortOrder?: string) => {
  if (field !== sortField) return "none" as const;
  return sortOrder?.toLowerCase() === "asc"
    ? ("ascending" as const)
    : ("descending" as const);
};

export const DataTableSortHeader = ({
  field,
  label,
  sortField,
  sortOrder,
  onSort,
  defaultSortOrder = "ASC",
  sortLabel = typeof label === "string" ? label : field,
  component: HeaderComponent = DataGridHeaderCell,
  headerProps,
  iconComponent: SortButton = TableHeaderIconButton,
}: DataTableSortHeaderProps) => {
  const active = field === sortField;
  const nextDirection =
    active && sortOrder?.toLowerCase() === "asc"
      ? "descending"
      : active
        ? "ascending"
        : defaultSortOrder.toLowerCase() === "desc"
          ? "descending"
          : "ascending";
  const sort = () => onSort(field);
  const ariaLabel = `Sort by ${sortLabel} ${nextDirection}`;

  return (
    <HeaderComponent
      {...headerProps}
      role="columnheader"
      aria-sort={getAriaSort(field, sortField, sortOrder)}
      aria-label={ariaLabel}
      onClick={sort}
    >
      <SortButton
        aria-label={ariaLabel}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          sort();
        }}
      >
        <FilterArrows
          column={field}
          sortField={sortField}
          sortOrder={sortOrder?.toUpperCase()}
          aria-hidden="true"
          focusable="false"
        />
      </SortButton>
      {label}
    </HeaderComponent>
  );
};

export interface DataTableProps<T> {
  "aria-label": string;
  header: ReactNode;
  rows: readonly T[];
  getRowId: (row: T, index: number) => string | number;
  renderRow: (row: T, index: number) => ReactNode;
  loading?: boolean;
  loadingMessage?: ReactNode;
  error?: ReactNode;
  emptyMessage?: ReactNode | false;
  beforeBody?: ReactNode;
  afterBody?: ReactNode;
  pagination?: Omit<PaginationProps, "aria-label"> & { "aria-label"?: string };
  minHeight?: string;
  className?: string;
  style?: CSSProperties;
  bodyProps?: HTMLAttributes<HTMLDivElement>;
  rootComponent?: ComponentType<any>;
}

export function DataTable<T>({
  "aria-label": ariaLabel,
  header,
  rows,
  getRowId,
  renderRow,
  loading = false,
  loadingMessage = "Loading...",
  error,
  emptyMessage = "No results found.",
  beforeBody,
  afterBody,
  pagination,
  minHeight,
  className,
  style,
  bodyProps,
  rootComponent: RootComponent = DataTableRoot,
}: DataTableProps<T>) {
  return (
    <>
      <RootComponent
        role="table"
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        $minHeight={minHeight}
        className={className}
        style={style}
      >
        {beforeBody}
        {header}
        {error && <DataTableMessage role="alert">{error}</DataTableMessage>}
        <DataTableBody
          {...bodyProps}
          role="rowgroup"
          $preserveHeightWhileLoading={loading && rows.length > 0}
        >
          {loading && (
            <DataTableLoadingRow>
              <DataTableLoadingCell>
                <DataTableLoadingStatus role="status" aria-live="polite">
                  <DataTableLoadingSpinner aria-hidden="true" />
                  {loadingMessage}
                </DataTableLoadingStatus>
              </DataTableLoadingCell>
            </DataTableLoadingRow>
          )}
          {rows.length > 0
            ? rows.map((row, index) => (
                <Fragment key={getRowId(row, index)}>
                  {renderRow(row, index)}
                </Fragment>
              ))
            : !loading &&
              emptyMessage !== false && (
                <DataTableEmptyMessage>{emptyMessage}</DataTableEmptyMessage>
              )}
          {afterBody}
        </DataTableBody>
      </RootComponent>
      {pagination && (
        <Pagination
          {...pagination}
          aria-label={pagination["aria-label"] || `${ariaLabel} pagination`}
        />
      )}
    </>
  );
}
